import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, UpdateStatusType, SettingsType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Tag } from '../../../../models/tag';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { EditBucketModalComponent, EditMode } from '../../edit-bucket/edit-bucket-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-tags-settings',
    templateUrl: './tags-settings.component.html'
})
export class TagsSettingsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;

    gridName = 'Tags';
    automationId = 'tags-settings';
    helpIndex: HelpIndex;

    isBusy = false;
    isBusyMessage = '';
    currentHandle = '';
    isPRJLevelSettings: boolean;
    updateStatusType = UpdateStatusType.TAG_VIEW;

    // permissions
    permissionType: ObjectActionListType;
    hasNewTagPermissions: boolean;
    hasCreateTagPermissions: boolean;
    hasDeleteTagPermissions: boolean;

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            sortBy: 'name',
            automationId: this.automationId,
            lockVisible: true,
            cellTemplateType: CellTemplateType.nameIconLabel,
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editTag(item)
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteTag(item)
        })
    ];

    rowClassRules = {
        'disabled-row': (): boolean => {
            return this.hasNewTagPermissions;
        }
    };

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listTags,
        responseName: 'tagList',
        data: {}
    });
    gridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });

    isSystemUser = false;

    addNewTag(): void {
        this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.TAG, projectHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
            if (result.modalResult) {
                this.genericGrid.loadData();
            }
        });
    }

    deleteTag(item: Tag): void {
        if (item) {
            const attr = item.attributes.find(a => a.name === 'documentCount');
            item.documentCount = attr ? parseInt(attr.value) : 0;
            item.ownerHandle = this.currentHandle;
            Object.assign(item, { systemScope: this.isSystemUser });
            this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
        }
    }

    editTag(item: Tag): void {
        this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.EDITTAG, projectHandle: this.currentHandle, entity: item, systemScope: this.isSystemUser } }).then((result) => {
            if (result.modalResult) {
                this.genericGrid.loadData();
            }
        });
    }

    responseArrayCallback = (response: any): Tag => new Tag(response);

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (rowNode && rowNode.data instanceof Tag) {
            this.editTag(rowNode.data);
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = projectHandle ? HelpIndex.myedisc_case_tagmgmt_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_tags_dlg : HelpIndex.org_tag_templates_org_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        this.permissionType = projectHandle ? ObjectActionListType.PRJ_TAGS : this.isSystemUser ? ObjectActionListType.SYSTEM_TAG_SETTINGS : ObjectActionListType.ORG_TAGS;
        this.contextMenuOptions[0].permissions = [{ permissionType: this.permissionType, permissionActions: [ActionType.CREATE] }];
        this.contextMenuOptions[1].permissions = [{
            permissionType: this.permissionType,
            permissionActions: [this.permissionType === ObjectActionListType.PRJ_TAGS ? ActionType.DELETE : ActionType.CREATE]
        }];

        this.hasCreateTagPermissions = Permissions.checkUserPermissions([{
            permissionType: this.permissionType,
            permissionActions: [ActionType.CREATE]
        }]);
        this.hasNewTagPermissions = Permissions.checkUserPermission(this.permissionType) && !this.hasCreateTagPermissions;
        this.hasDeleteTagPermissions = Permissions.checkUserPermissions([{
            permissionType: this.permissionType,
            permissionActions: [ActionType.DELETE]
        }]);

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasNewTagPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }
        this.isPRJLevelSettings = projectHandle ? true : false;
        if (this.currentHandle) {
            this.requestInfo.data = {
                projectHandle: this.currentHandle,
                count: 0,
                startIndex: 0,
                systemScope: this.isSystemUser
            };
            this.genericGrid.loadData();
        } else {
            this.requestInfo.data = { systemScope: this.isSystemUser };
        }
    }
}
